/**
 * Timeline View Data Component
 * Handles data fetching and rendering for timeline visualization
 * Separated to prevent header re-renders when data changes
 */

import React, { useMemo, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { generatePathDatacentral, paths } from '@/utils/path'

import { getOncallAssignments, getOncallSwaps } from '../../../../services'
import { ONCALL_QUERY_KEYS } from '../../../../constants'
import { TimelineDateRange, TimelineBarItem } from '../../../../types/timeline'
import { OncallSwapResponse } from '../../../../types/response'

import TimelineGrid from './TimelineGrid'
import MultiScheduleGrid from './MultiScheduleGrid'
import TimelineLegend from './TimelineLegend'
import AssignmentPopover from './AssignmentPopover'

import {
    groupAssignmentsByLayer,
    createTimelineDataset,
    extractUniqueEmployees,
    groupAssignmentsBySchedule,
} from '../utils/transformAssignments'

interface TimelineViewDataProps {
    scheduleId?: number
    dateRange: TimelineDateRange
}

const TimelineViewData: React.FC<TimelineViewDataProps> = ({
    scheduleId,
    dateRange,
}) => {
    const navigate = useNavigate()
    const { t: translate } = useTranslation('oncall')

    // State for popover
    const [selectedItem, setSelectedItem] = useState<TimelineBarItem | null>(
        null
    )
    const [popoverPosition, setPopoverPosition] = useState<{
        x: number
        y: number
    } | null>(null)

    // Fetch assignments for the date range
    const { data: assignmentsData, isLoading: loadingAssignments } =
        useQueryWithHandler({
            queryKey: [
                ONCALL_QUERY_KEYS.ASSIGNMENTS,
                'timeline',
                dateRange.start,
                dateRange.end,
                scheduleId,
            ],
            queryFn: () =>
                getOncallAssignments({
                    page: 1,
                    page_size: 100,
                    start_date: dateRange.start,
                    end_date: dateRange.end,
                    schedule_id: scheduleId,
                } as any),
        })

    // Fetch swaps for the date range
    const { data: swapsData, isLoading: loadingSwaps } = useQueryWithHandler({
        queryKey: [
            ONCALL_QUERY_KEYS.SWAPS,
            'timeline',
            dateRange.start,
            dateRange.end,
            scheduleId,
        ],
        queryFn: () =>
            getOncallSwaps({
                page: 1,
                page_size: 100,
                start_date: dateRange.start,
                end_date: dateRange.end,
                schedule_id: scheduleId,
            } as any),
    })

    const assignments = assignmentsData?.results || []
    const swaps: OncallSwapResponse[] = swapsData?.results || []
    const isLoading = loadingAssignments || loadingSwaps

    // Check if multi-schedule view (no scheduleId provided)
    const isMultiScheduleView = !scheduleId

    // Transform data for chart - single schedule
    const chartData = useMemo(() => {
        if (isMultiScheduleView) return []
        const layered = groupAssignmentsByLayer(assignments)
        return createTimelineDataset(layered, swaps)
    }, [assignments, swaps, isMultiScheduleView])

    // Transform data for multi-schedule view
    const scheduleGroups = useMemo(() => {
        if (!isMultiScheduleView) return []
        return groupAssignmentsBySchedule(assignments, swaps)
    }, [assignments, swaps, isMultiScheduleView])

    // Extract unique employees for legend
    const employees = useMemo(
        () => extractUniqueEmployees(assignments),
        [assignments]
    )

    // Bar click handler - show popover
    const handleBarClick = useCallback(
        (item: TimelineBarItem, event: React.MouseEvent) => {
            setSelectedItem(item)
            setPopoverPosition({ x: event.clientX, y: event.clientY })
        },
        []
    )

    // Close popover
    const handleClosePopover = useCallback(() => {
        setSelectedItem(null)
        setPopoverPosition(null)
    }, [])

    // View detail from popover
    const handleViewDetail = useCallback(
        (item: TimelineBarItem) => {
            handleClosePopover()
            if (item.assignmentId > 0) {
                navigate(
                    generatePathDatacentral(paths.oncallAssignmentDetail, {
                        id: item.assignmentId,
                    })
                )
            }
        },
        [navigate, handleClosePopover]
    )

    if (isLoading) {
        return (
            <div className="animate-pulse">
                <div className="h-[300px] bg-gray-100 rounded"></div>
            </div>
        )
    }

    if (assignments.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                <Typogaphy size="medium" className="mb-2">
                    {translate('no_assignments_in_range')}
                </Typogaphy>
                <Typogaphy size="small" className="text-gray-400">
                    {translate('try_different_date_range')}
                </Typogaphy>
            </div>
        )
    }

    return (
        <>
            {isMultiScheduleView ? (
                <MultiScheduleGrid
                    schedules={scheduleGroups}
                    dateRange={dateRange}
                    onBarClick={handleBarClick}
                />
            ) : (
                <TimelineGrid
                    data={chartData}
                    dateRange={dateRange}
                    onBarClick={handleBarClick}
                />
            )}

            <TimelineLegend
                employees={employees}
                showLayers={!isMultiScheduleView}
            />

            {/* Assignment/Swap Popover - shows details and actions on click */}
            <AssignmentPopover
                item={selectedItem}
                position={popoverPosition}
                onClose={handleClosePopover}
                onViewDetail={handleViewDetail}
            />
        </>
    )
}

export default TimelineViewData
